import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Input,
  Textarea,
  useDisclosure,
  Heading,
  IconButton,
  HStack,
  useToast,
  Spinner,
  Center,
  Switch,
  Flex,
  Text,
  Badge,
  Divider,
  SimpleGrid,
} from '@chakra-ui/react';
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
} from '@chakra-ui/table';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
} from '@chakra-ui/modal';
import {
  FormControl,
  FormLabel,
} from '@chakra-ui/form-control';
import { FaEdit, FaTrash, FaPlus } from 'react-icons/fa';

function getCookie(name) {
  let cookieValue = null;
  if (document.cookie && document.cookie !== '') {
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim();
      if (cookie.substring(0, name.length + 1) === (name + '=')) {
        cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
        break;
      }
    }
  }
  return cookieValue;
}

const FEATURE_SECTIONS = [
  {
    label: 'Core',
    color: 'blue',
    features: [
      { key: 'feature_events', label: 'Events', description: 'Event CRUD, instances, formats, regions' },
      { key: 'feature_bookings', label: 'Bookings', description: 'Bookings, waitlist, capacity management' },
      { key: 'feature_attendee_management', label: 'Attendee Management', description: 'Attendee CRM, notes, profile management' },
    ],
  },
  {
    label: 'Commerce',
    color: 'green',
    features: [
      { key: 'feature_payments', label: 'Payments', description: 'Stripe payments, shopping cart, credits' },
      { key: 'feature_memberships', label: 'Memberships', description: 'Membership tiers and subscriptions' },
      { key: 'feature_courses', label: 'Courses', description: 'Courses, sessions, enrollments, tutors' },
      { key: 'feature_coupons', label: 'Coupons', description: 'Coupons and vouchers' },
    ],
  },
  {
    label: 'Social',
    color: 'pink',
    features: [
      { key: 'feature_community_groups', label: 'Community Groups', description: 'Community groups, discussions, forums' },
      { key: 'feature_social_feed', label: 'Social Feed', description: 'Social feed, posts, follows, directory' },
      { key: 'feature_direct_messaging', label: 'Direct Messaging', description: 'Direct messages and conversations' },
    ],
  },
  {
    label: 'Networking',
    color: 'purple',
    features: [
      { key: 'feature_target_alerts', label: 'Target Alerts', description: 'Industry-based target alerts for attendees' },
      { key: 'feature_industry_clash', label: 'Industry Clash', description: 'Industry clash enforcement and clash queue' },
      { key: 'feature_space_fill', label: 'Space Fill', description: 'Space Fill smart attendee matching feature' },
    ],
  },
  {
    label: 'Operations',
    color: 'orange',
    features: [
      { key: 'feature_enquiries', label: 'Enquiries', description: 'Enquiries system (dashboard + portal)' },
      { key: 'feature_broadcasts', label: 'Broadcasts', description: 'Bulk email broadcast campaigns' },
      { key: 'feature_sms', label: 'SMS', description: 'SMS messages to attendees from the dashboard (sent through the messaging service)' },
      { key: 'feature_automation', label: 'Automation', description: 'Automation rules engine' },
      { key: 'feature_profile_tags', label: 'Profile Tags', description: 'Profile tags and attendee filters' },
      { key: 'feature_communications_inbox', label: 'Communications Inbox', description: 'Attendee-level threaded inbox with reply tracking' },
    ],
  },
  {
    label: 'Advanced',
    color: 'teal',
    features: [
      { key: 'feature_event_planner', label: 'Event Planner', description: 'Live rundown / Conductor / Event Planner' },
      { key: 'feature_conference_planning', label: 'Conference Planning', description: 'Multi-track/multi-room conference schedule builder and conference passes' },
      { key: 'feature_join_link', label: 'Join Link', description: '15-minute auto-register join link' },
      { key: 'feature_delegates_list', label: 'Delegates List', description: 'Post-event delegates list (Excel + GDPR)' },
      { key: 'feature_cms', label: 'CMS', description: 'CMS promo slots and pages' },
      { key: 'feature_webhooks', label: 'Webhooks', description: 'Webhook integrations and public API' },
      { key: 'feature_interactive_map', label: 'Interactive Map', description: 'Interactive region map dashboard widget' },
      { key: 'feature_qr_codes', label: 'QR Codes', description: 'QR code networking — create scannable codes, capture contacts' },
      { key: 'feature_ticketing', label: 'Ticketing Portal', description: 'Public ticketing portal — sell tickets without membership' },
      { key: 'feature_assistant', label: 'AI Assistant', description: 'Self-hosted AI chat assistant for read-only event Q&A' },
      { key: 'feature_signup_forms', label: 'Signup Forms', description: 'Public webinar/marketing signup form builder with embeddable forms' },
    ],
  },
];

const ALL_FEATURE_KEYS = FEATURE_SECTIONS.flatMap((s) => s.features.map((f) => f.key));

const EMPTY_FORM = {
  name: '',
  description: '',
  is_default: false,
  feature_events: true,
  feature_bookings: true,
  feature_attendee_management: true,
  feature_payments: false,
  feature_memberships: false,
  feature_courses: false,
  feature_coupons: false,
  feature_community_groups: false,
  feature_social_feed: false,
  feature_direct_messaging: false,
  feature_target_alerts: false,
  feature_industry_clash: false,
  feature_space_fill: false,
  feature_enquiries: false,
  feature_broadcasts: false,
  feature_sms: false,
  feature_automation: false,
  feature_profile_tags: false,
  feature_event_planner: false,
  feature_conference_planning: false,
  feature_join_link: false,
  feature_delegates_list: false,
  feature_cms: false,
  feature_webhooks: false,
  feature_interactive_map: false,
  feature_qr_codes: false,
  feature_communications_inbox: false,
  feature_ticketing: false,
  feature_assistant: false,
  feature_signup_forms: false,
};

const countEnabled = (formData) => ALL_FEATURE_KEYS.filter((k) => formData[k]).length;

const Verticals = () => {
  const [verticals, setVerticals] = useState([]);
  const [loading, setLoading] = useState(true);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const [selectedVertical, setSelectedVertical] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const toast = useToast();

  const fetchVerticals = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/v1/verticals/', {
        headers: { 'Authorization': `Token ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setVerticals(data);
      } else {
        toast({ title: 'Error fetching verticals', status: 'error', duration: 5000, isClosable: true });
      }
    } catch (error) {
      toast({ title: 'Error fetching verticals', description: error.message, status: 'error', duration: 5000, isClosable: true });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchVerticals();
  }, [fetchVerticals]);

  const handleEdit = (vertical) => {
    setSelectedVertical(vertical);
    setFormData({
      name: vertical.name || '',
      description: vertical.description || '',
      is_default: vertical.is_default || false,
      ...Object.fromEntries(ALL_FEATURE_KEYS.map((k) => [k, vertical[k] || false])),
    });
    onOpen();
  };

  const handleAdd = () => {
    setSelectedVertical(null);
    setFormData(EMPTY_FORM);
    onOpen();
  };

  const handleDeleteConfirm = (vertical) => {
    setSelectedVertical(vertical);
    onDeleteOpen();
  };

  const handleDelete = async () => {
    try {
      const token = localStorage.getItem('token');
      const csrftoken = getCookie('csrftoken');
      const response = await fetch(`/api/v1/verticals/${selectedVertical.id}/`, {
        method: 'DELETE',
        headers: { 'Authorization': `Token ${token}`, 'X-CSRFToken': csrftoken },
      });
      if (response.ok || response.status === 204) {
        toast({ title: 'Vertical deleted', status: 'success', duration: 3000, isClosable: true });
        onDeleteClose();
        fetchVerticals();
      } else {
        toast({ title: 'Error deleting vertical', status: 'error', duration: 5000, isClosable: true });
      }
    } catch (error) {
      toast({ title: 'Error deleting vertical', description: error.message, status: 'error', duration: 5000, isClosable: true });
    }
  };

  const handleSave = async () => {
    try {
      const token = localStorage.getItem('token');
      const csrftoken = getCookie('csrftoken');
      const url = selectedVertical ? `/api/v1/verticals/${selectedVertical.id}/` : '/api/v1/verticals/';
      const method = selectedVertical ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Token ${token}`,
          'X-CSRFToken': csrftoken,
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        toast({ title: `Vertical ${selectedVertical ? 'updated' : 'created'}`, status: 'success', duration: 3000, isClosable: true });
        onClose();
        fetchVerticals();
      } else {
        const errorData = await response.json();
        toast({ title: 'Error saving vertical', description: JSON.stringify(errorData), status: 'error', duration: 5000, isClosable: true });
      }
    } catch (error) {
      toast({ title: 'Error saving vertical', description: error.message, status: 'error', duration: 5000, isClosable: true });
    }
  };

  const handleFeatureToggle = (key) => {
    setFormData((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  if (loading) {
    return (
      <Center h="200px">
        <Spinner size="xl" color="purple.500" thickness="4px" />
      </Center>
    );
  }

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={6}>
        <Box>
          <Heading size="lg" color="gray.800">Verticals</Heading>
          <Text color="gray.500" mt={1}>Define product verticals and their feature sets. Changes sync to all API instances.</Text>
        </Box>
        <Button
          leftIcon={<FaPlus />}
          bgGradient="linear(to-r, purple.500, purple.600)"
          color="white"
          _hover={{ bgGradient: "linear(to-r, purple.600, purple.700)", transform: "translateY(-2px)", boxShadow: "lg" }}
          onClick={handleAdd}
          borderRadius="lg"
          px={6}
        >
          Add Vertical
        </Button>
      </Flex>

      <Box bg="white" borderRadius="xl" boxShadow="sm" borderWidth="1px" borderColor="gray.100" overflow="hidden">
        <Table variant="simple">
          <Thead bg="gray.50">
            <Tr>
              <Th color="gray.600" py={4}>Name</Th>
              <Th color="gray.600">Description</Th>
              <Th color="gray.600">Features Enabled</Th>
              <Th color="gray.600">Default</Th>
              <Th color="gray.600">Instances</Th>
              <Th color="gray.600" textAlign="right">Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {verticals.length === 0 ? (
              <Tr>
                <Td colSpan={6} textAlign="center" py={8} color="gray.500">
                  No verticals found. Click "Add Vertical" to create one.
                </Td>
              </Tr>
            ) : (
              verticals.map((v) => {
                const enabled = ALL_FEATURE_KEYS.filter((k) => v[k]).length;
                return (
                  <Tr key={v.id} _hover={{ bg: "gray.50" }} transition="background 0.2s">
                    <Td fontWeight="medium" color="gray.800">{v.name}</Td>
                    <Td color="gray.600" maxW="280px" isTruncated>
                      {v.description?.substring(0, 60)}{v.description?.length > 60 ? '...' : ''}
                    </Td>
                    <Td>
                      <Badge colorScheme="purple" borderRadius="full" px={3} py={1}>
                        {enabled} / {ALL_FEATURE_KEYS.length}
                      </Badge>
                    </Td>
                    <Td>
                      {v.is_default && (
                        <Badge colorScheme="teal" borderRadius="full" px={3} py={1}>Default</Badge>
                      )}
                    </Td>
                    <Td color="gray.600">{v.instance_count ?? 0}</Td>
                    <Td textAlign="right">
                      <HStack spacing={2} justify="flex-end">
                        <IconButton
                          aria-label="Edit vertical"
                          icon={<FaEdit />}
                          size="sm"
                          variant="ghost"
                          colorScheme="purple"
                          onClick={() => handleEdit(v)}
                        />
                        <IconButton
                          aria-label="Delete vertical"
                          icon={<FaTrash />}
                          size="sm"
                          variant="ghost"
                          colorScheme="red"
                          onClick={() => handleDeleteConfirm(v)}
                          isDisabled={v.is_default}
                        />
                      </HStack>
                    </Td>
                  </Tr>
                );
              })
            )}
          </Tbody>
        </Table>
      </Box>

      {/* Create / Edit Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="3xl" scrollBehavior="inside">
        <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(10px)" />
        <ModalContent borderRadius="xl">
          <ModalHeader borderBottomWidth="1px" py={4}>
            {selectedVertical ? 'Edit' : 'New'} Vertical
            {formData.name && (
              <Badge ml={3} colorScheme="purple" borderRadius="full" px={3}>
                {countEnabled(formData)} features enabled
              </Badge>
            )}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody py={6}>
            <FormControl isRequired>
              <FormLabel fontWeight="medium">Name</FormLabel>
              <Input
                value={formData.name}
                onChange={(e) => setFormData((p) => ({ ...p, name: e.target.value }))}
                borderRadius="lg"
                focusBorderColor="purple.500"
                placeholder="e.g. B2B Networking"
              />
            </FormControl>
            <FormControl mt={4}>
              <FormLabel fontWeight="medium">Description</FormLabel>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData((p) => ({ ...p, description: e.target.value }))}
                borderRadius="lg"
                focusBorderColor="purple.500"
                rows={3}
              />
            </FormControl>
            <FormControl mt={4} display="flex" alignItems="center">
              <FormLabel mb="0" fontWeight="medium">Default Vertical</FormLabel>
              <Switch
                isChecked={formData.is_default}
                onChange={(e) => setFormData((p) => ({ ...p, is_default: e.target.checked }))}
                colorScheme="teal"
              />
            </FormControl>

            <Divider my={6} />

            {FEATURE_SECTIONS.map((section) => (
              <Box key={section.label} mb={6}>
                <Text fontWeight="semibold" color="gray.700" mb={3}>
                  <Badge colorScheme={section.color} mr={2} borderRadius="full" px={2}>{section.label}</Badge>
                </Text>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                  {section.features.map((feature) => (
                    <Box
                      key={feature.key}
                      p={3}
                      borderWidth="1px"
                      borderRadius="lg"
                      borderColor={formData[feature.key] ? `${section.color}.300` : 'gray.200'}
                      bg={formData[feature.key] ? `${section.color}.50` : 'white'}
                      cursor="pointer"
                      onClick={() => handleFeatureToggle(feature.key)}
                      transition="all 0.15s"
                    >
                      <Flex align="center" justify="space-between">
                        <Box>
                          <Text fontWeight="medium" fontSize="sm">{feature.label}</Text>
                          <Text fontSize="xs" color="gray.500">{feature.description}</Text>
                        </Box>
                        <Switch
                          aria-label={feature.label}
                          isChecked={formData[feature.key]}
                          onChange={() => handleFeatureToggle(feature.key)}
                          colorScheme={section.color}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </Flex>
                    </Box>
                  ))}
                </SimpleGrid>
              </Box>
            ))}
          </ModalBody>

          <ModalFooter borderTopWidth="1px" py={4}>
            <Button variant="ghost" mr={3} onClick={onClose} borderRadius="lg">Cancel</Button>
            <Button
              bgGradient="linear(to-r, purple.500, purple.600)"
              color="white"
              _hover={{ bgGradient: "linear(to-r, purple.600, purple.700)" }}
              onClick={handleSave}
              borderRadius="lg"
              px={6}
            >
              {selectedVertical ? 'Update' : 'Create'} Vertical
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Confirmation */}
      <Modal isOpen={isDeleteOpen} onClose={onDeleteClose} size="sm">
        <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(10px)" />
        <ModalContent borderRadius="xl">
          <ModalHeader borderBottomWidth="1px" py={4}>Delete Vertical</ModalHeader>
          <ModalCloseButton />
          <ModalBody py={6}>
            <Text>
              Are you sure you want to delete <strong>{selectedVertical?.name}</strong>? Any instances using this
              vertical will be unlinked.
            </Text>
          </ModalBody>
          <ModalFooter borderTopWidth="1px" py={4}>
            <Button variant="ghost" mr={3} onClick={onDeleteClose} borderRadius="lg">Cancel</Button>
            <Button colorScheme="red" onClick={handleDelete} borderRadius="lg">Delete</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default Verticals;
